import { useState, useEffect, useRef } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import WhatsAppSendButton from '../../components/WhatsAppSendButton';
import { Plus, Trash2, Edit, Eye, X, Send, FileText, CheckCircle, Save, Package, ChevronDown, Printer } from 'lucide-react';

const UNITS = ['pcs','sqft','rft','kg','ltr','mtr','sheet','bag','box','ton','set','pair'];
const GST_RATES = ['0','5','12','18','28'];

const emptyItem = { item_name:'', description:'', quantity:'1', unit:'pcs', rate:'', tax_rate:'18' };
const emptyForm = {
  client_name:'', client_phone:'', client_address:'', client_gstin:'',
  project_id:'', challan_date: new Date().toISOString().slice(0,10),
  status:'draft', notes:'', discount_amount:'0',
  tax_mode:'cgst_sgst',
  manual_number:'', cpo_id:'', items:[{...emptyItem}],
};

function fmtCur(n){ return '₹'+Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2}); }
function fmt(d){ if(!d) return '—'; try{ return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); }catch{ return '—'; } }

function calcTotals(items, disc, tax_mode){
  const subtotal = items.reduce((s,i)=>s+(parseFloat(i.quantity)||0)*(parseFloat(i.rate)||0),0);
  const discAmt = parseFloat(disc)||0;
  // apply discount proportionally across items
  const discRatio = subtotal > 0 ? ((subtotal - discAmt) / subtotal) : 1;
  const totalGST = items.reduce((s,i)=>{
    const itemAmt = (parseFloat(i.quantity)||0)*(parseFloat(i.rate)||0)*discRatio;
    return s + itemAmt*(parseFloat(i.tax_rate)||0)/100;
  }, 0);
  const taxable = subtotal - discAmt;
  const cgst = tax_mode==='cgst_sgst' ? totalGST/2 : 0;
  const sgst = tax_mode==='cgst_sgst' ? totalGST/2 : 0;
  const igst = tax_mode==='igst' ? totalGST : 0;
  return { subtotal, discAmt, taxable, cgst, sgst, igst, totalGST, total: taxable+totalGST };
}

// ── Searchable Select ────────────────────────────────────────────────────────
function SearchableSelect({ options, value, onChange, placeholder, displayFn, valueFn, filterFn, className='' }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(()=>{
    const h = e => { if(ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return ()=>document.removeEventListener('mousedown', h);
  },[]);
  const selected = options.find(o=>String(valueFn(o))===String(value));
  const filtered = query ? options.filter(o=>filterFn(o,query)) : options;
  return (
    <div ref={ref} className={`relative ${className}`}>
      <div className="relative">
        <input
          className="field-input pr-8 cursor-pointer"
          placeholder={placeholder}
          value={open ? query : (selected ? displayFn(selected) : '')}
          onFocus={()=>{ setOpen(true); setQuery(''); }}
          onChange={e=>{ setQuery(e.target.value); setOpen(true); }}
        />
        <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
      </div>
      {open && (
        <div className="absolute z-[200] w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
          <div className="p-1">
            {filtered.length===0
              ? <p className="px-3 py-2 text-sm text-gray-400">No results found</p>
              : filtered.map((o,i)=>(
                <button key={i} type="button"
                  className={`w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-blue-50 transition-colors ${
                    String(valueFn(o))===String(value)?'bg-blue-50 text-blue-700 font-semibold':'text-gray-700'}`}
                  onClick={()=>{ onChange(o); setOpen(false); setQuery(''); }}
                >{displayFn(o)}</button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminSalesInvoicePage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('sales');
  const [challans, setChallans] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(emptyForm);
  const [saving, setSaving]     = useState(false);
  const [viewChallan, setViewChallan] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sendModal, setSendModal] = useState(null);
  const [sendClientId, setSendClientId] = useState('');
  const [sendType, setSendType] = useState('system');
  const [pdfFile, setPdfFile]   = useState(null);
  const [sending, setSending]   = useState(false);
  const [numberingInfo, setNumberingInfo] = useState({});
  const [projectItems, setProjectItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [cpoList, setCpoList] = useState([]);
  const [poQtyStatus, setPoQtyStatus] = useState(null); // { po_qty, sold_qty, remaining_qty, item_name }
  const pdfRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const [cRes,pRes] = await Promise.all([api.get('/sale-challans'),api.get('/projects')]);
      setChallans(cRes.data.filter(c=>c.challan_type==='sale'));
      setProjects(pRes.data.filter(p=>p.status!=='deleted'));
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const refreshNumberingInfo = () =>
    api.get('/numbering-info').then(r=>setNumberingInfo(r.data||{})).catch(()=>{});

  useEffect(()=>{
    load();
    api.get('/admin/clients-list').then(r=>setClients(r.data)).catch(()=>{});
    api.get('/admin/client-purchase-orders').then(r=>setCpoList(r.data||[])).catch(()=>{});
    refreshNumberingInfo();
  },[]);

  const totals = calcTotals(form.items, form.discount_amount, form.tax_mode);

  const setItem=(i,f,v)=>{ const n=[...form.items]; n[i]={...n[i],[f]:v}; setForm(x=>({...x,items:n})); };
  const addItem=()=>setForm(x=>({...x,items:[...x.items,{...emptyItem}]}));
  const removeItem=(i)=>{ if(form.items.length>1) setForm(x=>({...x,items:x.items.filter((_,idx)=>idx!==i)})); };

  const handleProjectChange = async (projectId) => {
    setProjectItems([]);
    if (!projectId) { setForm(f=>({...f, project_id:''})); return; }
    const proj = projects.find(p=>String(p.id)===String(projectId));
    setForm(f=>({
      ...f, project_id: projectId,
      client_name: (!f.client_name && proj?.client_name) ? proj.client_name : f.client_name,
      client_phone: (!f.client_phone && proj?.client_phone) ? proj.client_phone : f.client_phone,
    }));
    setLoadingItems(true);
    try {
      const res = await api.get(`/projects/${projectId}`);
      setProjectItems(res.data.items || []);
    } catch { /* silently ignore */ }
    finally { setLoadingItems(false); }
  };

  const applyProjectItems = () => {
    if (!projectItems.length) return;
    const mapped = projectItems.map(pi => ({
      item_name: pi.item_name || '',
      description: [pi.proto_code, pi.description || pi.dimensions].filter(Boolean).join(' · ') || '',
      quantity: String(pi.quantity || 1),
      unit: pi.unit || 'pcs',
      rate: pi.unit_price ? String(pi.unit_price) : '',
      tax_rate: '18',
    }));
    setForm(f=>({...f, items: mapped}));
    toast.success(`${mapped.length} items imported from project`);
  };

  const openCreate=()=>{
    setEditId(null);
    setProjectItems([]);
    setPoQtyStatus(null);
    setForm({...emptyForm, challan_date:new Date().toISOString().slice(0,10), manual_number:'', items:[{...emptyItem}]});
    refreshNumberingInfo();
    setShowModal(true);
  };
  const openEdit=async(id)=>{
    try{
      const res=await api.get(`/sale-challans/${id}`);
      const c=res.data;
      setEditId(id);
      const isIgst = (c.igst||0) > 0;
      const storedCgst = parseFloat(c.cgst_rate||c.cgst_percent||0);
      const storedSgst = parseFloat(c.sgst_rate||c.sgst_percent||0);
      const storedIgst = parseFloat(c.igst_rate||c.igst_percent||0);
      const derivedTaxRate = isIgst ? String(storedIgst||18) : String((storedCgst||9)+(storedSgst||9));
      setForm({
        client_name:c.client_name, client_phone:c.client_phone||'', client_address:c.client_address||'',
        client_gstin:c.client_gstin||'', project_id:c.project_id||'',
        challan_date:c.challan_date?.slice(0,10)||'', status:c.status, notes:c.notes||'',
        discount_amount:String(c.discount_amount||'0'),
        tax_mode: isIgst?'igst':'cgst_sgst',
        tax_rate: derivedTaxRate,
        cgst_rate: isIgst ? '' : String(storedCgst || (derivedTaxRate/2)),
        sgst_rate: isIgst ? '' : String(storedSgst || (derivedTaxRate/2)),
        igst_rate: isIgst ? String(storedIgst) : '',
        manual_number:'', cpo_id: c.cpo_id ? String(c.cpo_id) : '',
        items:(c.items||[]).map(i=>({item_name:i.item_name,description:i.description||'',quantity:String(i.quantity),unit:i.unit||'pcs',rate:String(i.rate||''),tax_rate:String(i.tax_rate||'18')})),
      });
      // Load qty status if this sale is linked to a PO
      if (c.cpo_id) {
        api.get(`/admin/client-purchase-orders/${c.cpo_id}/qty-status`)
          .then(r => setPoQtyStatus(r.data))
          .catch(() => setPoQtyStatus(null));
      } else {
        setPoQtyStatus(null);
      }
      setShowModal(true);
    }catch{ toast.error('Failed to load'); }
  };

  const handleSubmit=async(e)=>{
    e.preventDefault();
    setSaving(true);
    try{
      const payload={
        ...form, challan_type:'sale',
        subtotal:totals.subtotal, tax_amount:totals.cgst+totals.sgst+totals.igst,
        total_amount:totals.total,
        cgst:totals.cgst, sgst:totals.sgst, igst:totals.igst,
        discount_amount:parseFloat(form.discount_amount)||0,
        cpo_id: form.cpo_id || null,
      };
      if(editId){
        await api.put(`/sale-challans/${editId}`,payload);
        toast.success('Invoice updated');
        setShowModal(false); load();
      } else {
        const res = await api.post('/sale-challans',payload);
        // 202 = approval pending
        if(res.status === 202 || res.data?.approval_status === 'PENDING'){
          toast.success(`✅ Sale Invoice request submitted! Admin approval ke baad banega. (ID: #${res.data.approval_id})`);
          setShowModal(false);
        } else {
          toast.success('Invoice created');
          setShowModal(false); load();
        }
      }
    }catch(err){ toast.error(err.response?.data?.message||'Error'); }
    finally{ setSaving(false); }
  };

  const handleDelete=async()=>{
    try{ await api.delete(`/sale-challans/${deleteTarget}`); toast.success('Deleted'); setDeleteTarget(null); load(); }
    catch{ toast.error('Delete failed'); }
  };

  const openSend=(c)=>{ setSendModal(c); setSendClientId(''); setSendType('system'); setPdfFile(null); };

  const handleSend=async()=>{
    if(!sendClientId){ toast.error('Select a client'); return; }
    setSending(true);
    try{
      let pdfUrl = null;
      if(sendType==='pdf'){
        if(!pdfFile){ toast.error('Please select a PDF file'); setSending(false); return; }
        const fd=new FormData(); fd.append('pdf',pdfFile);
        const up=await api.post(`/sale-challans/${sendModal.id}/upload-pdf`,fd,{headers:{'Content-Type':'multipart/form-data'}});
        pdfUrl=up.data.pdf_url;
      }
      await api.patch(`/sale-challans/${sendModal.id}/send-to-client`,{client_id:sendClientId, send_type:sendType, pdf_url:pdfUrl});
      // Mark status as sent
      try { await api.patch(`/sale-challans/${sendModal.id}/status`, { status: 'sent' }); } catch {}
      toast.success('Invoice sent to client!');
      setSendModal(null); load();
    }catch(e){ toast.error(e.response?.data?.message||'Failed'); }
    finally{ setSending(false); }
  };

  const openPdf=async(url)=>{ try{ const r=await fetch(url); const b=await r.blob(); window.open(URL.createObjectURL(new Blob([b],{type:'application/pdf'})),'_blank'); }catch{ toast.error('Cannot open PDF'); } };

  // Load full invoice details when eye icon clicked
  const openView = async (c) => {
    setViewChallan(c);
    setViewLoading(true);
    try {
      const res = await api.get(`/sale-challans/${c.id}`);
      setViewChallan(res.data);
    } catch { /* use list data as fallback */ }
    finally { setViewLoading(false); }
  };

  // Formatted tax invoice HTML (used by Print and by WhatsApp)
  const buildInvoiceHtml = (d) => {
    if (!d) return '';
    const subtotal = parseFloat(d.subtotal||0);
    const discAmt  = parseFloat(d.discount_amount||0);
    const taxable  = subtotal - discAmt;
    const cgst     = parseFloat(d.cgst||0);
    const sgst     = parseFloat(d.sgst||0);
    const igst     = parseFloat(d.igst||0);
    const total    = parseFloat(d.total_amount||0);
    const isIgst   = igst > 0;
    const fC = n => '₹'+Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2});
    const fD = x => { try{ return new Date(x).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); }catch{ return x||''; } };
    const rows = (d.items||[]).map((it,i)=>{
      const lt = (parseFloat(it.quantity)||0)*(parseFloat(it.rate)||0);
      const tr = parseFloat(it.tax_rate||0);
      return `<tr style="border-bottom:1px solid #f1f5f9;background:${i%2?'#f8fafc':'white'}">
        <td style="padding:7px 8px;font-size:11px;color:#64748b">${i+1}</td>
        <td style="padding:7px 8px"><div style="font-size:12px;font-weight:600">${it.item_name||''}</div>${it.description?`<div style="font-size:10px;color:#64748b">${it.description}</div>`:''}</td>
        <td style="padding:7px 8px;font-size:12px;text-align:center">${it.quantity||''}</td>
        <td style="padding:7px 8px;font-size:11px;text-align:center;color:#64748b">${it.unit||'pcs'}</td>
        <td style="padding:7px 8px;font-size:12px;text-align:right">${fC(it.rate)}</td>
        <td style="padding:7px 8px;font-size:11px;text-align:center">${tr>0?tr+'%':'—'}</td>
        <td style="padding:7px 8px;font-size:12px;text-align:right;font-weight:600">${fC(lt)}</td>
      </tr>`;
    }).join('');
    const html = `<html><head><title>${d.challan_number}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:12px}
    @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}</style></head><body>
    <div style="max-width:720px;margin:0 auto;padding:24px">
      <div style="display:flex;justify-content:space-between;border-bottom:2px solid #7c3aed;padding-bottom:14px;margin-bottom:18px">
        <div><div style="font-size:20px;font-weight:800;color:#7c3aed">${d.company_name||'MOZI INNOVATORS LLP'}</div>
          ${d.company_address?`<div style="font-size:11px;color:#555;margin-top:2px">${d.company_address}</div>`:''}
          ${d.company_phone?`<div style="font-size:11px;color:#555">📞 ${d.company_phone}</div>`:''}
          ${d.company_gstin?`<div style="font-size:11px;color:#555">GSTIN: ${d.company_gstin}</div>`:''}</div>
        <div style="text-align:right"><div style="font-size:16px;font-weight:700;color:#7c3aed">TAX INVOICE</div>
          <div style="font-size:14px;font-weight:600;margin-top:2px">${d.challan_number}</div>
          <div style="font-size:11px;color:#6b7280;margin-top:4px">Date: ${fD(d.challan_date)}</div></div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:18px">
        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:12px">
          <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#64748b;margin-bottom:5px">Bill To</div>
          <div style="font-size:13px;font-weight:700">${d.client_name||''}</div>
          ${d.client_phone?`<div style="font-size:11px;color:#475569;margin-top:2px">📞 ${d.client_phone}</div>`:''}
          ${d.client_address?`<div style="font-size:11px;color:#475569;margin-top:2px">${d.client_address}</div>`:''}
          ${d.client_gstin?`<div style="font-size:11px;color:#475569;margin-top:2px">GSTIN: ${d.client_gstin}</div>`:''}
        </div>
        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:12px">
          <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#64748b;margin-bottom:5px">Invoice Details</div>
          ${d.project_name?`<div style="font-size:11px;margin-bottom:2px"><b>Project:</b> ${d.project_name}</div>`:''}
          <div style="font-size:11px;margin-bottom:2px"><b>Invoice No:</b> ${d.challan_number}</div>
          <div style="font-size:11px"><b>Date:</b> ${fD(d.challan_date)}</div>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;margin-bottom:14px">
        <thead><tr style="background:#7c3aed;color:white">
          <th style="padding:8px;text-align:left;font-size:11px">#</th>
          <th style="padding:8px;text-align:left;font-size:11px">Item</th>
          <th style="padding:8px;text-align:center;font-size:11px">Qty</th>
          <th style="padding:8px;text-align:center;font-size:11px">Unit</th>
          <th style="padding:8px;text-align:right;font-size:11px">Rate</th>
          <th style="padding:8px;text-align:center;font-size:11px">GST%</th>
          <th style="padding:8px;text-align:right;font-size:11px">Amount</th>
        </tr></thead><tbody>${rows}</tbody>
      </table>
      <div style="display:flex;justify-content:flex-end;margin-bottom:18px">
        <div style="min-width:250px">
          <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;color:#374151;border-bottom:1px solid #f1f5f9"><span>Subtotal</span><span>${fC(subtotal)}</span></div>
          ${discAmt>0?`<div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;color:#ef4444;border-bottom:1px solid #f1f5f9"><span>Discount</span><span>— ${fC(discAmt)}</span></div>`:''}
          <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;font-weight:600;color:#1e293b;border-bottom:1px solid #e2e8f0"><span>Taxable Value</span><span>${fC(taxable)}</span></div>
          ${!isIgst&&cgst>0?`<div style="display:flex;justify-content:space-between;padding:3px 0;font-size:12px"><span>CGST</span><span>${fC(cgst)}</span></div>`:''}
          ${!isIgst&&sgst>0?`<div style="display:flex;justify-content:space-between;padding:3px 0;font-size:12px;border-bottom:1px solid #f1f5f9"><span>SGST</span><span>${fC(sgst)}</span></div>`:''}
          ${isIgst&&igst>0?`<div style="display:flex;justify-content:space-between;padding:3px 0;font-size:12px;border-bottom:1px solid #f1f5f9"><span>IGST</span><span>${fC(igst)}</span></div>`:''}
          <div style="display:flex;justify-content:space-between;padding:8px 0 4px;font-size:15px;font-weight:700;color:#7c3aed;border-top:2px solid #7c3aed;margin-top:4px"><span>TOTAL</span><span>${fC(total)}</span></div>
        </div>
      </div>
      ${d.notes?`<div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:6px;padding:10px 14px;margin-bottom:18px;font-size:11px;color:#5b21b6"><b>Notes:</b> ${d.notes}</div>`:''}
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px;margin-top:32px;padding-top:14px;border-top:1px solid #e2e8f0">
        ${['Prepared By','Authorised Signatory',"Receiver's Signature"].map(l=>`<div style="text-align:center"><div style="border-top:1px solid #94a3b8;margin-top:40px;padding-top:6px;font-size:10px;color:#64748b">${l}</div></div>`).join('')}
      </div>
    </div></body></html>`;
    return html;
  };

  // Print formatted tax invoice
  const handleAdminPrint = (d) => {
    const html = buildInvoiceHtml(d);
    if (!html) return;
    const win = window.open('','_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(()=>win.print(), 500);
  };

  const filtered = challans.filter(c=>!search||(c.challan_number||'').toLowerCase().includes(search.toLowerCase())||(c.client_name||'').toLowerCase().includes(search.toLowerCase()));

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="text-xl font-black text-gray-900">Sales Invoices</h1><p className="text-gray-400 text-sm">Tax invoices sent to clients</p></div>
        {canWrite && <button onClick={openCreate} className="btn-primary"><Plus size={16}/> New Invoice</button>}
      </div>

      {/* Search */}
      <div className="card p-4 flex gap-3">
        <input className="field-input flex-1" placeholder="Search invoice or client..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"/></div>
        ) : filtered.length===0 ? (
          <div className="py-16 text-center"><FileText className="w-10 h-10 text-gray-200 mx-auto mb-3"/><p className="text-gray-400">No sales invoices yet</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead><tr>
                <th className="text-left">Invoice #</th>
                <th className="text-left">Client</th>
                <th className="text-left">Date</th>
                <th className="text-right">Total</th>
                <th className="text-center">Status</th>
                <th className="text-center">Sent</th>
                <th className="text-center">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(c=>(
                  <tr key={c.id}>
                    <td className="font-mono font-bold text-purple-700">{c.challan_number}</td>
                    <td className="font-medium">{c.client_name}</td>
                    <td className="text-gray-500">{fmt(c.challan_date)}</td>
                    <td className="text-right font-semibold">{fmtCur(c.total_amount)}</td>
                    <td className="text-center"><span className={`text-xs px-2 py-1 rounded-full font-semibold ${c.status==='draft'?'bg-gray-100 text-gray-600':c.status==='sent'?'bg-blue-100 text-blue-700':'bg-green-100 text-green-700'}`}>{c.status}</span></td>
                    <td className="text-center">
                      {c.sent_to_client ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto"/> : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={()=>openView(c)} className="text-amber-600 hover:text-amber-700" title="View &amp; Print"><Eye size={14}/></button>
                        {canEdit && <button onClick={()=>openEdit(c.id)} className="text-blue-600 hover:text-blue-700" title="Edit"><Edit size={14}/></button>}
                        <button onClick={()=>openSend(c)} className="text-teal-600 hover:text-teal-700" title="Send to Client"><Send size={14}/></button>
                        {canDelete && <button onClick={()=>setDeleteTarget(c.id)} className="text-red-400 hover:text-red-600" title="Delete"><Trash2 size={14}/></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Invoice Modal */}
      {viewChallan && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:720}}>
              <div className="modal-header">
                <div>
                  <h2>Tax Invoice</h2>
                  <p>{viewChallan.challan_number} &mdash; {viewChallan.client_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={()=>handleAdminPrint(viewChallan)}
                    className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors">
                    <Printer size={14}/> Print Invoice
                  </button>
                  <WhatsAppSendButton
                    documentType="SALE_INVOICE"
                    referenceId={viewChallan.challan_number || viewChallan.id}
                    fileName={`Invoice ${viewChallan.challan_number || viewChallan.id}.pdf`}
                    defaultPhone={viewChallan.client_phone}
                    getHtml={async () => buildInvoiceHtml(viewChallan)}
                    className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors"
                  />
                  <button className="modal-close" onClick={()=>setViewChallan(null)}><X size={16}/></button>
                </div>
              </div>
              <div className="modal-body space-y-4">
                {viewLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"/>
                  </div>
                ) : (
                  <>
                    {/* Info */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        ['Invoice #', viewChallan.challan_number],
                        ['Date', fmt(viewChallan.challan_date)],
                        ['Client', viewChallan.client_name],
                        ['Subtotal', fmtCur(viewChallan.subtotal)],
                        ...(parseFloat(viewChallan.discount_amount)>0?[['Discount', `— ${fmtCur(viewChallan.discount_amount)}`]]:[]),
                        ['Total', fmtCur(viewChallan.total_amount)],
                      ].map(([k,v])=>(
                        <div key={k} className={`rounded-xl p-3 ${k==='Total'?'bg-purple-50 border border-purple-100':'bg-gray-50'}`}>
                          <p className="text-xs text-gray-400 mb-0.5">{k}</p>
                          <p className={`text-sm font-semibold ${k==='Total'?'text-purple-700':'text-gray-900'}`}>{v}</p>
                        </div>
                      ))}
                    </div>
                    {/* Tax */}
                    {(parseFloat(viewChallan.cgst)>0||parseFloat(viewChallan.sgst)>0||parseFloat(viewChallan.igst)>0) && (
                      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                        <p className="text-xs font-bold text-blue-700 mb-2">Tax Breakdown</p>
                        <div className="flex gap-6">
                          {parseFloat(viewChallan.cgst)>0 && <div><p className="text-xs text-gray-400">CGST</p><p className="text-sm font-semibold">{fmtCur(viewChallan.cgst)}</p></div>}
                          {parseFloat(viewChallan.sgst)>0 && <div><p className="text-xs text-gray-400">SGST</p><p className="text-sm font-semibold">{fmtCur(viewChallan.sgst)}</p></div>}
                          {parseFloat(viewChallan.igst)>0 && <div><p className="text-xs text-gray-400">IGST</p><p className="text-sm font-semibold">{fmtCur(viewChallan.igst)}</p></div>}
                        </div>
                      </div>
                    )}
                    {/* Items */}
                    {viewChallan.items?.length > 0 && (
                      <div>
                        <p className="text-sm font-bold text-gray-900 mb-2">Items ({viewChallan.items.length})</p>
                        <div className="border border-gray-100 rounded-xl overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-xs font-semibold text-gray-400 uppercase">
                              <tr>
                                <th className="text-left px-3 py-2">Item</th>
                                <th className="text-center px-2 py-2">Qty</th>
                                <th className="text-right px-2 py-2">Rate</th>
                                <th className="text-center px-2 py-2">GST%</th>
                                <th className="text-right px-3 py-2">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {viewChallan.items.map((it,i)=>{
                                const lt=(parseFloat(it.quantity)||0)*(parseFloat(it.rate)||0);
                                return (
                                  <tr key={i} className={`border-t border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/50'}`}>
                                    <td className="px-3 py-2">
                                      <p className="font-semibold">{it.item_name}</p>
                                      {it.description && <p className="text-xs text-gray-400">{it.description}</p>}
                                    </td>
                                    <td className="text-center px-2 py-2 text-gray-500">{it.quantity} {it.unit}</td>
                                    <td className="text-right px-2 py-2 text-gray-500">{fmtCur(it.rate)}</td>
                                    <td className="text-center px-2 py-2 text-blue-600 font-medium">{it.tax_rate||0}%</td>
                                    <td className="text-right px-3 py-2 font-semibold">{fmtCur(lt)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {viewChallan.notes && (
                      <div className="bg-gray-50 rounded-xl p-3"><p className="text-xs text-gray-400 mb-1">Notes</p><p className="text-sm text-gray-700">{viewChallan.notes}</p></div>
                    )}
                    <div className="flex justify-end">
                      <button onClick={()=>handleAdminPrint(viewChallan)}
                        className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors">
                        <Printer size={15}/> Print / Save as PDF
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:780,display:'flex',flexDirection:'column',maxHeight:'92vh'}}>
              <div className="modal-header">
                <div><h2>{editId?'Edit Invoice':'New Sales Invoice'}</h2></div>
                <button className="modal-close" onClick={()=>setShowModal(false)}><X size={16}/></button>
              </div>
              <div style={{overflowY:'auto',flex:1}}>
              <form onSubmit={handleSubmit} className="modal-body space-y-4">
                {/* Invoice Number — manual or auto based on settings */}
                {!editId && numberingInfo['sale']?.mode === 'manual' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <label className="field-label text-amber-800">Invoice Number (Manual) <span className="text-red-500">*</span></label>
                    <input
                      required
                      className="field-input font-mono"
                      placeholder={numberingInfo['sale']?.last_used ? `After: ${numberingInfo['sale'].last_used}` : 'e.g. INV-2025-001'}
                      value={form.manual_number}
                      onChange={e=>setForm(f=>({...f,manual_number:e.target.value}))}
                    />
                    {numberingInfo['sale']?.last_used && (
                      <p className="text-xs text-amber-600 mt-1">Last used: <b>{numberingInfo['sale'].last_used}</b></p>
                    )}
                  </div>
                )}
                {!editId && numberingInfo['sale']?.mode === 'auto' && numberingInfo['sale']?.next_number && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-purple-700">Auto Numbering</p>
                    <p className="text-sm font-mono font-bold text-purple-900 mt-0.5">Next: {numberingInfo['sale'].next_number}</p>
                    <p className="text-xs text-purple-500 mt-0.5">Invoice number will be assigned automatically on save</p>
                  </div>
                )}
                {/* Link to Client PO */}
                <div>
                  <label className="field-label">Link to Client Purchase Order (optional)</label>
                  <SearchableSelect
                    options={[{id:'',po_number:'',client_name:'— No PO —'}, ...cpoList]}
                    value={form.cpo_id}
                    onChange={async cpo => {
                      setForm(f => ({
                        ...f,
                        cpo_id: cpo.id ? String(cpo.id) : '',
                        client_name: (cpo.id && !f.client_name) ? (cpo.client_name||f.client_name) : f.client_name,
                      }));
                      if (cpo.id) {
                        try {
                          const r = await api.get(`/admin/client-purchase-orders/${cpo.id}/qty-status`);
                          setPoQtyStatus(r.data);
                        } catch { setPoQtyStatus(null); }
                      } else {
                        setPoQtyStatus(null);
                      }
                    }}
                    placeholder="Search PO number or client..."
                    displayFn={cpo => cpo.id ? `PO #${cpo.po_number} — ${cpo.client_name}` : '— No PO —'}
                    valueFn={cpo => cpo.id}
                    filterFn={(cpo,q) => !cpo.id || (cpo.po_number||'').toLowerCase().includes(q.toLowerCase()) || (cpo.client_name||'').toLowerCase().includes(q.toLowerCase())}
                  />

                  {/* PO Qty Status Panel */}
                  {form.cpo_id && poQtyStatus && (() => {
                    const { po_qty, sold_qty, remaining_qty, item_name } = poQtyStatus;
                    const currentSaleQty = form.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0), 0);
                    const wouldExceed = remaining_qty !== null && currentSaleQty > remaining_qty;
                    return (
                      <div className={`mt-2 rounded-xl border p-3 text-xs space-y-2 ${wouldExceed ? 'bg-red-50 border-red-300' : 'bg-teal-50 border-teal-200'}`}>
                        <p className={`font-bold ${wouldExceed ? 'text-red-700' : 'text-teal-700'}`}>
                          {wouldExceed ? '⚠ Exceeding PO quantity!' : '✓ PO Quantity Tracker'}
                        </p>
                        {item_name && <p className="text-gray-500">Item: <strong className="text-gray-700">{item_name}</strong></p>}
                        <div className="grid grid-cols-3 gap-2">
                          <div className="bg-white rounded-lg p-2 text-center border border-gray-100">
                            <p className="text-gray-400">PO Qty</p>
                            <p className="font-bold text-gray-800 text-base">{po_qty}</p>
                          </div>
                          <div className="bg-white rounded-lg p-2 text-center border border-gray-100">
                            <p className="text-gray-400">Already Sold</p>
                            <p className="font-bold text-amber-600 text-base">{sold_qty}</p>
                          </div>
                          <div className={`rounded-lg p-2 text-center border ${remaining_qty <= 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                            <p className="text-gray-400">Remaining</p>
                            <p className={`font-bold text-base ${remaining_qty <= 0 ? 'text-red-600' : 'text-green-700'}`}>{remaining_qty ?? '∞'}</p>
                          </div>
                        </div>
                        {remaining_qty !== null && (
                          <div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full transition-all ${sold_qty >= po_qty ? 'bg-red-500' : 'bg-amber-500'}`}
                                style={{ width: `${Math.min(Math.round(sold_qty/po_qty*100),100)}%` }} />
                            </div>
                            <p className="text-gray-400 mt-0.5">{Math.min(Math.round(sold_qty/po_qty*100),100)}% of PO already billed</p>
                          </div>
                        )}
                        {wouldExceed && (
                          <p className="text-red-600 font-semibold">
                            This sale has {currentSaleQty} qty but only {remaining_qty} remaining against this PO.
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {form.cpo_id && !poQtyStatus && (
                    <p className="text-xs text-teal-600 mt-1">✓ Invoice will be tracked against this PO for fulfillment</p>
                  )}
                </div>

                {/* Client — searchable */}
                <div>
                  <label className="field-label">Client Name *</label>
                  <SearchableSelect
                    options={clients}
                    value={form.client_name}
                    onChange={c => setForm(f => ({...f,
                      client_name: c.name,
                      client_phone: c.phone||c.contact_number||f.client_phone,
                      client_address: c.address||f.client_address,
                      client_gstin: c.gstin||f.client_gstin,
                    }))}
                    placeholder="Search client..."
                    displayFn={c => c.name}
                    valueFn={c => c.name}
                    filterFn={(c,q) => (c.name||'').toLowerCase().includes(q.toLowerCase()) || (c.phone||'').includes(q)}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="field-label">Phone</label><input className="field-input" value={form.client_phone} onChange={e=>setForm(f=>({...f,client_phone:e.target.value}))}/></div>
                  <div><label className="field-label">Address</label><input className="field-input" value={form.client_address} onChange={e=>setForm(f=>({...f,client_address:e.target.value}))}/></div>
                  <div><label className="field-label">GSTIN</label><input className="field-input" placeholder="GST number" value={form.client_gstin} onChange={e=>setForm(f=>({...f,client_gstin:e.target.value}))}/></div>
                </div>

                {/* Project — searchable + Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="field-label">Project (optional)</label>
                    <SearchableSelect
                      options={[{id:'',project_id:'',name:'— No Project —'},...projects]}
                      value={form.project_id}
                      onChange={p => handleProjectChange(p.id)}
                      placeholder="Search project..."
                      displayFn={p => p.id ? `${p.project_id} — ${p.name}` : '— No Project —'}
                      valueFn={p => p.id}
                      filterFn={(p,q) => !p.id || (p.project_id||'').toLowerCase().includes(q.toLowerCase()) || (p.name||'').toLowerCase().includes(q.toLowerCase())}
                    />
                    {loadingItems && <p className="text-xs text-purple-500 mt-1">Loading project items...</p>}
                    {!loadingItems && projectItems.length > 0 && (
                      <button type="button" onClick={applyProjectItems}
                        className="mt-1.5 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg py-1.5 transition-colors">
                        <Package size={12}/> Import {projectItems.length} items from project
                      </button>
                    )}
                  </div>
                  <div><label className="field-label">Invoice Date *</label><input required type="date" className="field-input" value={form.challan_date} onChange={e=>setForm(f=>({...f,challan_date:e.target.value}))}/></div>
                </div>

                {/* Items */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="field-label mb-0">Items *</label>
                    <button type="button" onClick={addItem} className="flex items-center gap-1 text-xs font-semibold text-teal-700 border border-teal-300 px-2 py-1 rounded-lg"><Plus size={12}/> Add Row</button>
                  </div>
                  <div className="grid gap-1 px-1 pb-1 text-xs font-semibold text-gray-400 uppercase" style={{gridTemplateColumns:'2fr 1.5fr 0.7fr 0.7fr 1fr 1fr auto'}}>
                    <span>Item</span><span>Description</span><span>Qty</span><span>Unit</span><span>Rate (₹)</span><span>GST %</span><span/>
                  </div>
                  <div className="space-y-2">
                    {form.items.map((it,idx)=>{
                      const lineTotal = (parseFloat(it.quantity)||0)*(parseFloat(it.rate)||0);
                      const lineGST = lineTotal*(parseFloat(it.tax_rate)||0)/100;
                      return (
                        <div key={idx} className="bg-gray-50 rounded-xl p-2 border border-gray-100 space-y-1">
                          <div className="grid gap-2" style={{gridTemplateColumns:'2fr 1.5fr 0.7fr 0.7fr 1fr 1fr auto'}}>
                            <input className="field-input text-sm" placeholder="Item name" value={it.item_name} onChange={e=>setItem(idx,'item_name',e.target.value)}/>
                            <input className="field-input text-sm" placeholder="Description" value={it.description} onChange={e=>setItem(idx,'description',e.target.value)}/>
                            <input type="number" className="field-input text-sm" placeholder="Qty" value={it.quantity} onChange={e=>setItem(idx,'quantity',e.target.value)}/>
                            <select className="field-input text-sm" value={it.unit} onChange={e=>setItem(idx,'unit',e.target.value)}>{UNITS.map(u=><option key={u}>{u}</option>)}</select>
                            <input type="number" className="field-input text-sm" placeholder="Rate" value={it.rate} onChange={e=>setItem(idx,'rate',e.target.value)}/>
                            <div>
                              <select className="field-input text-sm" value={it.tax_rate} onChange={e=>setItem(idx,'tax_rate',e.target.value)}>
                                {GST_RATES.map(r=><option key={r} value={r}>{r}%</option>)}
                              </select>
                            </div>
                            <button type="button" onClick={()=>removeItem(idx)} className="text-red-400 hover:text-red-600 px-1"><Trash2 size={13}/></button>
                          </div>
                          {lineTotal > 0 && (
                            <div className="flex items-center justify-end gap-4 text-xs px-1">
                              <span className="text-gray-400">Amount: <b className="text-gray-700">{fmtCur(lineTotal)}</b></span>
                              {lineGST > 0 && <span className="text-blue-500">GST ({it.tax_rate}%): <b>{fmtCur(lineGST)}</b></span>}
                              <span className="text-green-700 font-semibold">= {fmtCur(lineTotal + lineGST)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Tax & Discount */}
                <div className="bg-blue-50 rounded-xl p-4 space-y-3 border border-blue-100">
                  <p className="text-xs font-bold text-blue-700 uppercase tracking-wide">Tax & Discount</p>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                      <input type="radio" name="tax_mode" checked={form.tax_mode==='cgst_sgst'} onChange={()=>setForm(f=>({...f,tax_mode:'cgst_sgst'}))}/>
                      CGST + SGST (Intra-state)
                    </label>
                    <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                      <input type="radio" name="tax_mode" checked={form.tax_mode==='igst'} onChange={()=>setForm(f=>({...f,tax_mode:'igst'}))}/>
                      IGST (Inter-state)
                    </label>
                  </div>
                  <div>
                    <label className="field-label">Overall Discount (₹)</label>
                    <input type="number" className="field-input" placeholder="0" value={form.discount_amount} onChange={e=>setForm(f=>({...f,discount_amount:e.target.value}))}/>
                    <p className="text-xs text-blue-500 mt-1">GST per item is set in the Rate % column above. Discount is applied proportionally before GST.</p>
                  </div>
                  {/* Auto-calculated summary */}
                  <div className="bg-white rounded-xl p-3 space-y-1 border border-blue-100 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Subtotal (Rate × Qty)</span><span className="font-semibold">{fmtCur(totals.subtotal)}</span></div>
                    {totals.discAmt>0 && <div className="flex justify-between text-red-500"><span>Discount</span><span>— {fmtCur(totals.discAmt)}</span></div>}
                    <div className="flex justify-between font-semibold text-gray-800 border-t border-blue-100 pt-1"><span>Taxable Value</span><span>{fmtCur(totals.taxable)}</span></div>
                    {form.tax_mode==='cgst_sgst' ? <>
                      {totals.cgst>0 && <div className="flex justify-between text-blue-600"><span>CGST (mixed rates / 2)</span><span>{fmtCur(totals.cgst)}</span></div>}
                      {totals.sgst>0 && <div className="flex justify-between text-blue-600"><span>SGST (mixed rates / 2)</span><span>{fmtCur(totals.sgst)}</span></div>}
                    </> : <>
                      {totals.igst>0 && <div className="flex justify-between text-blue-600"><span>IGST (mixed rates)</span><span>{fmtCur(totals.igst)}</span></div>}
                    </>}
                    <div className="flex justify-between font-bold text-base border-t pt-1 mt-1"><span>Total</span><span className="text-green-700">{fmtCur(totals.total)}</span></div>
                  </div>
                </div>

                <div><label className="field-label">Notes</label><textarea className="field-input" rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>

                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={saving} className="btn-primary flex-1"><Save size={15}/> {saving?'Saving...':'Save Invoice'}</button>
                  <button type="button" onClick={()=>setShowModal(false)} className="btn-secondary flex-1">Cancel</button>
                </div>
              </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send to Client Modal */}
      {sendModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:480}}>
              <div className="modal-header">
                <div><h2>Send Invoice to Client</h2><p>{sendModal.challan_number}</p></div>
                <button className="modal-close" onClick={()=>setSendModal(null)}><X size={16}/></button>
              </div>
              <div className="modal-body space-y-4">
                <div>
                  <label className="field-label">Select Client *</label>
                  <select className="field-input" value={sendClientId} onChange={e=>setSendClientId(e.target.value)}>
                    <option value="">— Choose Client —</option>
                    {clients.map(c=><option key={c.id} value={c.id}>{c.name} {c.email?`(${c.email})`:''}</option>)}
                  </select>
                </div>

                <div>
                  <label className="field-label">Send Method *</label>
                  <div className="space-y-2 mt-1">
                    <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${sendType==='system'?'border-teal-500 bg-teal-50':'border-gray-200 bg-gray-50'}`}>
                      <input type="radio" name="send_type" checked={sendType==='system'} onChange={()=>setSendType('system')} className="mt-0.5"/>
                      <div>
                        <p className="font-bold text-sm text-gray-900">System Print</p>
                        <p className="text-xs text-gray-500">Client sees a formatted invoice view on their portal</p>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${sendType==='pdf'?'border-indigo-500 bg-indigo-50':'border-gray-200 bg-gray-50'}`}>
                      <input type="radio" name="send_type" checked={sendType==='pdf'} onChange={()=>setSendType('pdf')} className="mt-0.5"/>
                      <div>
                        <p className="font-bold text-sm text-gray-900">Uploaded PDF</p>
                        <p className="text-xs text-gray-500">Client gets a download button for your PDF</p>
                      </div>
                    </label>
                  </div>
                </div>

                {sendType==='pdf' && (
                  <div>
                    <label className="field-label">Upload Invoice PDF *</label>
                    <div
                      className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${pdfFile ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-indigo-400 hover:bg-indigo-50'}`}
                      onClick={() => pdfRef.current?.click()}
                    >
                      {pdfFile ? (
                        <div>
                          <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2"/>
                          <p className="text-sm font-semibold text-green-700">{pdfFile.name}</p>
                          <p className="text-xs text-green-500">{(pdfFile.size/1024/1024).toFixed(2)} MB</p>
                          <button type="button"
                            onClick={e=>{ e.stopPropagation(); setPdfFile(null); if(pdfRef.current) pdfRef.current.value=''; }}
                            className="mt-1.5 text-xs text-red-500 hover:text-red-700 underline"
                          >Remove file</button>
                        </div>
                      ) : (
                        <div>
                          <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2"/>
                          <p className="text-sm text-gray-500">Click to upload PDF</p>
                          <p className="text-xs text-gray-400">Max 20MB · PDF only</p>
                        </div>
                      )}
                    </div>
                    <input ref={pdfRef} type="file" accept=".pdf,application/pdf" className="hidden"
                      onChange={e=>{ if(e.target.files[0]) setPdfFile(e.target.files[0]); }}/>
                  </div>
                )}

                <div className="flex gap-3 pt-1">
                  <button onClick={handleSend} disabled={sending} className="btn-primary flex-1"><Send size={14}/> {sending?'Sending...':'Send to Client'}</button>
                  <button onClick={()=>setSendModal(null)} className="btn-secondary">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="modal-backdrop">
          <div className="modal-scroll" style={{alignItems:'center'}}>
            <div className="confirm-box">
              <div className="confirm-icon bg-red-100"><Trash2 className="text-red-500" size={22}/></div>
              <h3 className="font-bold text-gray-900 mb-1">Delete Invoice?</h3>
              <p className="text-sm text-gray-500 mb-5">This cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={handleDelete} className="btn-danger flex-1">Delete</button>
                <button onClick={()=>setDeleteTarget(null)} className="btn-secondary flex-1">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
